// src/core/rituales.js — capa de datos de los rituales del grupo.
//
// FASE 4 · F1.3.d1. `arengas`, `cabalas`, `promesas` y `reacciones_ritual`
// viven en Supabase Postgres. Los rituales son por jugador (player_id) dentro
// de un grupo.
//
// Lecturas: SELECT directo — la RLS filtra la visibilidad (un miembro ve todo
// lo del grupo; los no-miembros solo lo público no bajado / no vencido).
//
// Escrituras con reglas pasan por RPCs `SECURITY DEFINER`:
//   - crear_arenga    — una arenga pública exige el pacto del grupo certificado;
//                       genera audio_id placeholder (el audio real entra en F1.4).
//   - reaccionar      — reacción polimórfica; 1 reacción real por (jugador,
//                       ritual); 'al_pasto' (reporte) convive aparte.
//   - validar_promesa — validación grupal: un integrante != creador cierra la
//                       promesa (la RLS de UPDATE es solo del creador).
// El resto (crear cábala/promesa, extender) es INSERT/UPDATE directo permitido
// por la RLS. El scoring (puntos_rituales) lo hacen triggers server-side.

import { supabase } from '../supabase.js'
import { queryWithTimeout, NET_TIMEOUT_MS } from '../utils/timeout.js'

// ─── Caché en memoria de los rituales del grupo (Bug #1 · skeleton + caché) ─
// Cachea TODAS las lecturas que arma la vista de rituales (arengas, cábalas,
// promesas, reacciones y arengas guardadas) para que un cambio de tab dentro
// del grupo sea instantáneo: cero fetches durante el TTL. El primer ingreso al
// grupo es la única carga que toca la red.
//
// `memoConTTL` cachea por clave Y deduplica requests en vuelo: si dos renders
// concurrentes (el guardian dispara embedRituales más de una vez al entrar al
// grupo) piden lo mismo, comparten un único fetch en lugar de duplicarlo. Se
// invalida al crear/mutar un ritual; con Realtime (F1.4.b) la invalidación
// pasará a ser por push y este TTL deja de hacer falta.
const RITUALES_CACHE_TTL_MS = 60_000

const memoConTTL = (ttl) => {
  const cache = new Map()    // clave → { value, at }
  const inflight = new Map() // clave → Promise (request en vuelo, aún sin resolver)
  return {
    run: async (clave, fn) => {
      const hit = cache.get(clave)
      if (hit && Date.now() - hit.at < ttl) return hit.value
      if (inflight.has(clave)) return inflight.get(clave)
      const promise = (async () => {
        try {
          const value = await fn()
          cache.set(clave, { value, at: Date.now() })
          return value
        } finally {
          inflight.delete(clave)
        }
      })()
      inflight.set(clave, promise)
      return promise
    },
    clear: () => { cache.clear(); inflight.clear() },
  }
}

const _ritualesMemo = memoConTTL(RITUALES_CACHE_TTL_MS)    // clave: groupId
const _reaccionesMemo = memoConTTL(RITUALES_CACHE_TTL_MS)  // clave: ritualTipo|ids
const _guardadasMemo = memoConTTL(RITUALES_CACHE_TTL_MS)   // clave: userId

// Invalida TODA la caché de rituales. La llaman las mutaciones (crear /
// reaccionar / validar / extender / borrar): tras un cambio, el próximo
// render del grupo vuelve a la red. El argumento (groupId) se acepta por
// compatibilidad con los callers pero se ignora — se limpia todo.
export const invalidarCacheRituales = () => {
  _ritualesMemo.clear()
  _reaccionesMemo.clear()
  _guardadasMemo.clear()
}

// Trae arengas + cábalas + promesas del grupo en un lote paralelo, cacheado.
// Dentro del TTL devuelve el lote sin tocar la red.
const cargarRitualesDelGrupo = async (groupId) => {
  if (!groupId) return { arengas: [], cabalas: [], promesas: [] }
  return _ritualesMemo.run(groupId, async () => {
    const [arengas, cabalas, promesas] = await Promise.all([
      _fetchArengasDelGrupo(groupId),
      _fetchCabalasDelGrupo(groupId),
      _fetchPromesasDelGrupo(groupId),
    ])
    return { arengas, cabalas, promesas }
  })
}

// ─── Arengas ────────────────────────────────────────────────────────────────

// Crea una arenga. `data` =
// { tipo_arenga, titulo, es_publica, duracion_segundos, audio_url, audio_id }.
// audio_url y audio_id son opcionales. El RPC tiene defaults: audio_url='pendiente'
// y audio_id se genera server-side si el cliente no lo pasa. El cliente del
// grabador SÍ los pasa: necesita el audio_id estable para construir el path
// {group_id}/{audio_id}.{ext} en el bucket antes del INSERT.
// Devuelve { data } | { error }.
export const createArenga = async (groupId, creadorId, data) => {
  const { data: row, error } = await supabase.rpc('crear_arenga', {
    p_group_id: groupId,
    p_creador_id: creadorId,
    p_tipo_arenga: data.tipo_arenga,
    p_titulo: data.titulo ?? null,
    p_es_publica: Boolean(data.es_publica),
    p_duracion_segundos: data.duracion_segundos,
    p_audio_url: data.audio_url ?? 'pendiente',
    p_audio_id: data.audio_id ?? null,
  })
  if (!error) invalidarCacheRituales(groupId)
  return error ? { error } : { data: row }
}

// Cambia la visibilidad pública/privada de una arenga.
// El RPC `toggle_arenga_visibilidad` valida que el caller sea el creador.
export const toggleArengaVisibilidad = async (arengaId, esPublica) => {
  const { data, error } = await supabase.rpc('toggle_arenga_visibilidad', {
    p_arenga_id: arengaId,
    p_es_publica: Boolean(esPublica),
  })
  if (!error) invalidarCacheRituales()
  return error ? { error } : { data }
}

// Elimina una arenga. Dos pasos:
//   1. RPC eliminar_arenga (SECURITY DEFINER) borra SOLO la fila y devuelve
//      audio_path. Valida que auth.uid() sea el creador.
//   2. supabase.storage.from('arengas-audio').remove([path]) limpia el archivo.
//      Best-effort: si falla, queda huérfano (~5 MB), pero la fila ya se fue.
//      La policy DELETE del bucket exige "solo creador" — el caller cumple
//      porque el paso 1 ya validó la propiedad.
// Por qué dos pasos: Supabase bloquea DELETE directo a storage.objects desde
// SQL ("Direct deletion from storage tables is not allowed"). DT-CL anotada
// para migrar a Edge Function (atomicidad real) post-25-may.
export const eliminarArengaCompleta = async (arengaId) => {
  const { data, error } = await supabase.rpc('eliminar_arenga', {
    p_arenga_id: arengaId,
  })
  if (error) return { error }
  invalidarCacheRituales()
  const audioPath = data?.audio_path
  if (audioPath) {
    const { error: rmErr } = await supabase.storage.from('arengas-audio').remove([audioPath])
    if (rmErr) console.warn('[rituales] eliminar_arenga: archivo huérfano:', audioPath, rmErr.message)
  }
  return { data }
}

// Lectura cruda (sin caché) — la usa cargarRitualesDelGrupo.
const _fetchArengasDelGrupo = async (groupId) => {
  if (!groupId) return []
  const { data, error } = await supabase
    .from('arengas')
    .select('*, jugadores(display_name)')
    .eq('group_id', groupId)
    .order('created_at', { ascending: false })
  if (error) { console.error('[rituales] getArengasDelGrupo:', error.message); return [] }
  return data || []
}

// Arengas del grupo — servidas desde la caché de rituales (TTL corto).
export const getArengasDelGrupo = async (groupId) => (await cargarRitualesDelGrupo(groupId)).arengas

export const getArenga = async (arengaId) => {
  if (!arengaId) return null
  const { data, error } = await supabase
    .from('arengas')
    .select('*, jugadores(display_name)')
    .eq('arenga_id', arengaId)
    .maybeSingle()
  if (error) { console.error('[rituales] getArenga:', error.message); return null }
  return data || null
}

// Busca una arenga por audio_id (el slug URL-safe de 12 chars usado por el
// link viral pactomundial.futbol/t/AUDIO_ID). FASE 3.17 Bloque D.
// Para visitante anon o logueado-no-miembro de la arenga privada, la RLS
// devuelve null (no podemos distinguir "no existe" de "es privada inaccesible"
// sin un RPC SECURITY DEFINER — el caller trata ambos casos igual).
export const getArengaPorAudioId = async (audioId) => {
  if (!audioId) return null
  const { data, error } = await supabase
    .from('arengas')
    .select('*, jugadores(display_name), grupos(name, code)')
    .eq('audio_id', audioId)
    .maybeSingle()
  if (error) { console.error('[rituales] getArengaPorAudioId:', error.message); return null }
  return data || null
}

// DELETE directo — la RLS solo permite borrar las arengas propias.
export const deleteArenga = async (arengaId) => {
  const { error } = await supabase.from('arengas').delete().eq('arenga_id', arengaId)
  if (!error) invalidarCacheRituales()
  return error ? { error } : { data: true }
}

// Cuenta rituales creados por un jugador (DT-CQ, F1.4.b.2). 3 selects paralelos
// con COUNT exact + head:true (no trae filas). NO cacheado: el perfil es una
// vista poco frecuente y los counts cambian con cualquier creación.
export const contarMisRituales = async (playerId) => {
  if (!playerId) return { arengas: 0, cabalas: 0, promesas: 0 }
  const [a, c, p] = await Promise.all([
    supabase.from('arengas').select('arenga_id', { count: 'exact', head: true }).eq('creador_id', playerId),
    supabase.from('cabalas').select('cabala_id', { count: 'exact', head: true }).eq('creador_id', playerId),
    supabase.from('promesas').select('promesa_id', { count: 'exact', head: true }).eq('creador_id', playerId),
  ])
  return {
    arengas: a.count || 0,
    cabalas: c.count || 0,
    promesas: p.count || 0,
  }
}

// Aplica la curva logarítmica del doc Scoring Ritualístico v1.0 §5.1 sobre
// N reacciones acumuladas. Devuelve el total de puntos generados por esas N.
// Espejo en JS de aplicar_curva_logaritmica(g) de DB — usar SIEMPRE el mismo
// algoritmo para que el "+N pts" mostrado coincida con la spec.
export const curvaAcumulada = (n) => {
  let total = 0
  for (let g = 0; g < n; g++) {
    const sig = g + 1
    const step = sig <= 10 ? 1 : sig <= 1000 ? 10 : sig <= 10000 ? 100 : sig <= 100000 ? 1000 : 10000
    if (sig % step === 0) total += 1
  }
  return total
}

// Scoring Ritualístico v1.0 §3 R1 + §5.1 + §7: puntos acumulados por UN
// ritual al creador. Decisión del dueño (29-may): la curva §5.1 corre por
// ritual — cada arenga/cábala/promesa tiene su propia curva sobre SUS
// reacciones. Esto difiere del backend actual (`fn_on_reaccion_creada` aplica
// curva AGREGADA por jugador) — DT post-piloto, ver getResumenRitualesPorTipo.
//
// reaccionesTotal incluye al_pasto (R4 §10.3 — al_pasto cuenta como reacción
// a efectos de scoring del creador).
// esIncumplida solo aplica a promesas en estado 'incumplida' (§7 R8 → -100).
export const puntosPorRitual = ({ reaccionesTotal = 0, esIncumplida = false } = {}) =>
  1 + curvaAcumulada(reaccionesTotal) + (esIncumplida ? -100 : 0)

// Actualiza in-place TODOS los slots "Puntos: +X" de un ritual (atributo
// data-rit-pts="<tipo>:<id>") + el contador "N reac." si existe (atributo
// data-arenga-reac="<id>" usado por arenga-card.js en mosaico Tribuna).
// Cada slot guarda data-rit-reac con el conteo previo: este helper suma
// deltaReac, recalcula puntosPorRitual y reescribe el textContent. Se llama
// desde handlers de reacción después del optimistic update de los botones.
//
// Resuelve el bug "card no sube hasta cerrar/abrir el modal": applyReactionDelta
// solo actualizaba botones de reacción (selector .{tipo}-react), pero los slots
// "Puntos: +X" viven en la card del mosaico y en la card cerrada (NO son botones
// react). Este helper los cubre con un selector dedicated.
export const refrescarContadoresRitual = (ritualTipo, ritualId, deltaReac) => {
  if (typeof document === 'undefined') return
  // 1) Actualizar todos los slots "Puntos: +X" del ritual.
  const slotsPts = document.querySelectorAll(`[data-rit-pts="${ritualTipo}:${ritualId}"]`)
  for (const slot of slotsPts) {
    const reacAntes = parseInt(slot.dataset.ritReac || '0', 10) || 0
    const reacAhora = Math.max(0, reacAntes + deltaReac)
    slot.dataset.ritReac = String(reacAhora)
    const esIncumplida = slot.dataset.ritIncumplida === '1'
    const pts = puntosPorRitual({ reaccionesTotal: reacAhora, esIncumplida })
    slot.textContent = `Puntos: ${pts > 0 ? '+' : ''}${pts}`
  }
  // 2) Si es arenga, también actualizar el "N reac." de la card mosaico.
  if (ritualTipo === 'arenga') {
    const slotsReac = document.querySelectorAll(`[data-arenga-reac="${ritualId}"]`)
    for (const slot of slotsReac) {
      const cur = parseInt(slot.dataset.arengaReacCount || '0', 10) || 0
      const nuevo = Math.max(0, cur + deltaReac)
      slot.dataset.arengaReacCount = String(nuevo)
      slot.textContent = `${nuevo} reac.`
    }
  }
}

// Resumen "Mis Arengas / Cábalas / Promesas" del perfil. Por user_id (todos
// los memberships del usuario, simétrico al historial tribunero / DT-EP).
// Para cada tipo: { creadas, reaccionesRecibidas, puntos } — donde puntos
// es la SUMA de puntosPorRitual(r) para cada ritual del jugador. La curva
// §5.1 corre por ritual (decisión del dueño 29-may): cada arenga/cábala/
// promesa tiene su propia curva sobre SUS reacciones. Esto reemplaza el
// enfoque anterior (curva agregada por tipo) que infraestimaba puntos en
// rituales virales múltiples. DT-ER cerrada por este refactor.
//
// IMPORTANTE: el backend `puntos_rituales.puntos_total` SIGUE calculándose
// con curva agregada por jugador (fn_on_reaccion_creada lee total acumulado
// del jugador). Eso es la fuente canónica para ranking competitivo. El
// "+N pts" del perfil y de las cards usa el modelo por-ritual (esta función
// + puntosPorRitual). Discrepancia documentada → DT post-piloto: migrar
// backend a curva-por-ritual + recalcular puntos_rituales.
export const getResumenRitualesPorTipo = async (userId) => {
  const vacio = {
    arenga:  { creadas: 0, reaccionesRecibidas: 0, puntos: 0 },
    cabala:  { creadas: 0, reaccionesRecibidas: 0, puntos: 0 },
    promesa: { creadas: 0, reaccionesRecibidas: 0, puntos: 0, incumplidas: 0 },
  }
  if (!userId) return vacio
  // 1) Resolver todos los player_id del user (un player_id por membership).
  const jugRes = await supabase
    .from('jugadores')
    .select('player_id')
    .eq('user_id', userId)
  if (jugRes.error) { console.error('[rituales] getResumenRitualesPorTipo (jugadores):', jugRes.error.message); return vacio }
  const playerIds = (jugRes.data || []).map((j) => j.player_id)
  if (!playerIds.length) return vacio
  // 2) IDs (y estado para promesa) de rituales del user por tipo.
  const [arRes, caRes, prRes] = await Promise.all([
    supabase.from('arengas').select('arenga_id').in('creador_id', playerIds),
    supabase.from('cabalas').select('cabala_id').in('creador_id', playerIds),
    supabase.from('promesas').select('promesa_id, estado').in('creador_id', playerIds),
  ])
  const arengaIds = (arRes.data || []).map((r) => r.arenga_id)
  const cabalaIds = (caRes.data || []).map((r) => r.cabala_id)
  const promesaList = prRes.data || []
  const promesaIds = promesaList.map((r) => r.promesa_id)
  const incumplidaSet = new Set(promesaList.filter((r) => r.estado === 'incumplida').map((r) => r.promesa_id))
  // 3) Reacciones por ritual (3 queries, agrupando por ritual_id).
  const fetchReacs = async (tipo, ids) => {
    if (!ids.length) return new Map()
    const { data, error } = await supabase
      .from('reacciones_ritual')
      .select('ritual_id')
      .eq('ritual_tipo', tipo)
      .in('ritual_id', ids)
    if (error) { console.error('[rituales] fetchReacs:', error.message); return new Map() }
    const m = new Map()
    for (const r of data || []) m.set(r.ritual_id, (m.get(r.ritual_id) || 0) + 1)
    return m
  }
  const [arReacs, caReacs, prReacs] = await Promise.all([
    fetchReacs('arenga', arengaIds),
    fetchReacs('cabala', cabalaIds),
    fetchReacs('promesa', promesaIds),
  ])
  // 4) Sumar puntosPorRitual por cada uno (suma de cards individuales).
  const aggregar = (ids, reacsMap, esIncumplidaFn = () => false) => {
    let puntos = 0, reaccionesRecibidas = 0
    for (const id of ids) {
      const reac = reacsMap.get(id) || 0
      reaccionesRecibidas += reac
      puntos += puntosPorRitual({ reaccionesTotal: reac, esIncumplida: esIncumplidaFn(id) })
    }
    return { puntos, reaccionesRecibidas }
  }
  const aSum = aggregar(arengaIds, arReacs)
  const cSum = aggregar(cabalaIds, caReacs)
  const pSum = aggregar(promesaIds, prReacs, (id) => incumplidaSet.has(id))
  return {
    arenga:  { creadas: arengaIds.length,  reaccionesRecibidas: aSum.reaccionesRecibidas, puntos: aSum.puntos },
    cabala:  { creadas: cabalaIds.length,  reaccionesRecibidas: cSum.reaccionesRecibidas, puntos: cSum.puntos },
    promesa: { creadas: promesaIds.length, reaccionesRecibidas: pSum.reaccionesRecibidas, puntos: pSum.puntos, incumplidas: incumplidaSet.size },
  }
}

// Perfil completo en UNA sola llamada (RPC perfil_completo, SECURITY DEFINER).
// Colapsa los ~7 round-trips en serie que hacía modules/perfil.js (puntos +
// counts + resumen [3 olas] + guardadas + 2 my-reaction + miPlayerIds) en un
// único viaje. Alcance NACIONAL (todos los player_id del user, dedup por user),
// igual que ranking_nacional. El banner rituales = SUM(puntos_total) guardado
// (idéntico al ranking nacional); las tarjetas por tipo (cards) suman EXACTO al
// banner porque la penalización se toma del guardado, no se recuenta.
//
// p_player_id (grupo activo, opcional) solo se usa para validación; el resto es
// por user_id. Devuelve el JSON crudo del RPC; el caller lo adapta al shape de
// render. Si el RPC falla, devuelve null (el caller pinta el estado vacío).
export const getPerfilCompleto = async (userId, playerId = null) => {
  if (!userId) return null
  // DT-H3-resto: timeout. En error/timeout LANZA → renderPerfil muestra
  // "Reintentar" (distinto de data=null, que es perfil vacío legítimo).
  let data, error
  try {
    ({ data, error } = await queryWithTimeout(
      (s) => supabase.rpc('perfil_completo', { p_user_id: userId, p_player_id: playerId }).abortSignal(s),
      NET_TIMEOUT_MS, 'perfil_completo'))
  } catch (err) { console.error('[rituales] getPerfilCompleto timeout:', err?.message || err); throw new Error('perfil_completo: timeout') }
  if (error) { console.error('[rituales] getPerfilCompleto:', error.message); throw new Error(`perfil_completo: ${error.message}`) }
  return data || null
}

// ─── Cábalas ────────────────────────────────────────────────────────────────

// Crea una cábala. INSERT directo (la RLS lo permite a miembros del grupo).
// Las cábalas NO se editan ni se borran (no hay policy UPDATE/DELETE).
// `data` = { texto, visibilidad, expira_en (ISO) }. Devuelve { data } | { error }.
export const createCabala = async (groupId, creadorId, data) => {
  const { data: rows, error } = await supabase
    .from('cabalas')
    .insert({
      group_id: groupId,
      creador_id: creadorId,
      texto: data.texto,
      visibilidad: data.visibilidad || 'privada',
      expira_en: data.expira_en,
    })
    .select()
  if (!error) invalidarCacheRituales(groupId)
  return error ? { error } : { data: rows?.[0] }
}

const _fetchCabalasDelGrupo = async (groupId) => {
  if (!groupId) return []
  const { data, error } = await supabase
    .from('cabalas')
    .select('*, jugadores(display_name)')
    .eq('group_id', groupId)
    .order('created_at', { ascending: false })
  if (error) { console.error('[rituales] getCabalasDelGrupo:', error.message); return [] }
  return data || []
}

// Cábalas del grupo — servidas desde la caché de rituales (TTL corto).
export const getCabalasDelGrupo = async (groupId) => (await cargarRitualesDelGrupo(groupId)).cabalas

// Solo las cábalas todavía vigentes (expira_en en el futuro).
export const getCabalasActivas = async (groupId) => {
  const todas = await getCabalasDelGrupo(groupId)
  const ahora = Date.now()
  return todas.filter((c) => new Date(c.expira_en).getTime() > ahora)
}

// ─── Promesas ───────────────────────────────────────────────────────────────

// Crea una promesa. INSERT directo. Estado inicial 'activa'.
// `data` = { condicion, compromiso, visibilidad, deadline_at (ISO) }.
export const createPromesa = async (groupId, creadorId, data) => {
  const { data: rows, error } = await supabase
    .from('promesas')
    .insert({
      group_id: groupId,
      creador_id: creadorId,
      condicion: data.condicion,
      compromiso: data.compromiso,
      visibilidad: data.visibilidad || 'privada',
      deadline_at: data.deadline_at,
    })
    .select()
  if (!error) invalidarCacheRituales(groupId)
  return error ? { error } : { data: rows?.[0] }
}

const _fetchPromesasDelGrupo = async (groupId) => {
  if (!groupId) return []
  const { data, error } = await supabase
    .from('promesas')
    .select('*, jugadores!promesas_creador_id_fkey(display_name)')
    .eq('group_id', groupId)
    .order('created_at', { ascending: false })
  if (error) { console.error('[rituales] getPromesasDelGrupo:', error.message); return [] }
  return data || []
}

// Promesas del grupo — servidas desde la caché de rituales (TTL corto).
export const getPromesasDelGrupo = async (groupId) => (await cargarRitualesDelGrupo(groupId)).promesas

// Validación grupal — un integrante distinto del creador cierra la promesa.
export const marcarCumplida = async (promesaId, validadorId, evidencia = null) => {
  const { data, error } = await supabase.rpc('validar_promesa', {
    p_promesa_id: promesaId, p_validador_id: validadorId,
    p_resultado: 'cumplida', p_evidencia: evidencia,
  })
  if (!error) invalidarCacheRituales()
  return error ? { error } : { data }
}

export const marcarIncumplida = async (promesaId, validadorId, evidencia = null) => {
  const { data, error } = await supabase.rpc('validar_promesa', {
    p_promesa_id: promesaId, p_validador_id: validadorId,
    p_resultado: 'incumplida', p_evidencia: evidencia,
  })
  if (!error) invalidarCacheRituales()
  return error ? { error } : { data }
}

// Extiende el plazo de una promesa (acción del creador — UPDATE directo).
// `data` = { deadline_at (ISO), extension_label }.
export const extenderPromesa = async (promesaId, data) => {
  const { data: rows, error } = await supabase
    .from('promesas')
    .update({
      estado: 'en_extension',
      deadline_at: data.deadline_at,
      extended_at: new Date().toISOString(),
      extension_label: data.extension_label ?? null,
    })
    .eq('promesa_id', promesaId)
    .select()
  if (!error) invalidarCacheRituales()
  return error ? { error } : { data: rows?.[0] }
}

// Declaración del creador sobre su propia promesa (UPDATE directo — la RLS de
// `promesas` solo deja al creador). `estado` ∈ 'pendiente_validacion' (declara
// que la cumplió y espera la validación grupal) | 'incumplida' (la da por
// perdida). El cierre a 'cumplida' lo hace otro integrante vía marcarCumplida.
export const declararPromesa = async (promesaId, estado) => {
  const patch = { estado }
  if (estado === 'incumplida') patch.closed_at = new Date().toISOString()
  const { data: rows, error } = await supabase
    .from('promesas')
    .update(patch)
    .eq('promesa_id', promesaId)
    .select()
  if (!error) invalidarCacheRituales()
  return error ? { error } : { data: rows?.[0] }
}

// ─── Reacciones (polimórficas: arenga | cabala | promesa) ──────────────────

// Reacciona a un ritual vía el RPC reaccionar. `tipoReaccion` libre
// ('escuchen_esto', 'vamo_arriba', 'bendita', 'me_sumo'…); 'al_pasto' = reporte.
export const reaccionar = async (playerId, ritualTipo, ritualId, tipoReaccion) => {
  const { data, error } = await supabase.rpc('reaccionar', {
    p_player_id: playerId,
    p_ritual_tipo: ritualTipo,
    p_ritual_id: ritualId,
    p_tipo_reaccion: tipoReaccion,
  })
  if (!error) invalidarCacheRituales()
  return error ? { error } : { data }
}

// "Al pasto" — reporte de moderación comunitaria (3+ bajan el ritual, vía cron).
export const marcarAlPasto = (playerId, ritualTipo, ritualId) =>
  reaccionar(playerId, ritualTipo, ritualId, 'al_pasto')

// Reacciones de un ritual puntual.
export const getReaccionesDelRitual = async (ritualTipo, ritualId) => {
  if (!ritualId) return []
  const { data, error } = await supabase
    .from('reacciones_ritual')
    .select('*')
    .eq('ritual_tipo', ritualTipo)
    .eq('ritual_id', ritualId)
  if (error) { console.error('[rituales] getReaccionesDelRitual:', error.message); return [] }
  return data || []
}

// Reacciones de varios rituales del mismo tipo de una sola query (evita N+1).
export const getReaccionesPorRituales = async (ritualTipo, ritualIds) => {
  if (!ritualIds || !ritualIds.length) return []
  const clave = `${ritualTipo}|${[...ritualIds].sort().join(',')}`
  return _reaccionesMemo.run(clave, async () => {
    const { data, error } = await supabase
      .from('reacciones_ritual')
      .select('*')
      .eq('ritual_tipo', ritualTipo)
      .in('ritual_id', ritualIds)
    if (error) { console.error('[rituales] getReaccionesPorRituales:', error.message); return [] }
    return data || []
  })
}

// Quita la reacción "real" (todo lo que no sea 'al_pasto') del jugador a un
// ritual. Lo usa el toggle-off de la spec arengas v1.2 §10: clickear la misma
// reacción que ya tenías → quita. La policy DELETE de reacciones_ritual valida
// `soy_este_jugador(player_id)` server-side — el cliente solo manda el DELETE.
export const quitarReaccion = async (playerId, ritualTipo, ritualId) => {
  if (!playerId || !ritualId) return { error: { message: 'parametros_faltantes' } }
  const { error } = await supabase
    .from('reacciones_ritual')
    .delete()
    .eq('player_id', playerId)
    .eq('ritual_tipo', ritualTipo)
    .eq('ritual_id', ritualId)
    .neq('tipo_reaccion', 'al_pasto')
  if (!error) invalidarCacheRituales()
  return error ? { error } : { data: true }
}

// Mis reacciones (las reales, no 'al_pasto') a varios rituales del mismo tipo.
// Devuelve { ritual_id → tipo_reaccion }. Para el feed de tribuna y la lista
// del perfil — evita N+1 queries en la marca visual del botón activo.
// Generalizada en el bloque auditoría Cábalas/Promesas: el ritualTipo es
// param, y los aliases por-tipo abajo preservan la API histórica.
export const getMisReacciones = async (playerId, ritualTipo, ritualIds) => {
  if (!playerId || !ritualIds || !ritualIds.length) return {}
  const { data, error } = await supabase
    .from('reacciones_ritual')
    .select('ritual_id, tipo_reaccion')
    .eq('player_id', playerId)
    .eq('ritual_tipo', ritualTipo)
    .in('ritual_id', ritualIds)
    .neq('tipo_reaccion', 'al_pasto')
  if (error) { console.error('[rituales] getMisReacciones:', error.message); return {} }
  const out = {}
  for (const row of data || []) out[row.ritual_id] = row.tipo_reaccion
  return out
}

// Set de ritual_ids a los que el jugador ya reportó al_pasto. Spec arengas
// v1.2 §9 / promesas v1.0 §15: 1 reporte por jugador. El UNIQUE constraint
// en DB previene duplicados pero el front necesita el estado previo para no
// inflar el contador con clicks repetidos. Cábalas NO tienen al_pasto.
export const getMisAlPasto = async (playerId, ritualTipo, ritualIds) => {
  if (!playerId || !ritualIds || !ritualIds.length) return new Set()
  const { data, error } = await supabase
    .from('reacciones_ritual')
    .select('ritual_id')
    .eq('player_id', playerId)
    .eq('ritual_tipo', ritualTipo)
    .in('ritual_id', ritualIds)
    .eq('tipo_reaccion', 'al_pasto')
  if (error) { console.error('[rituales] getMisAlPasto:', error.message); return new Set() }
  return new Set((data || []).map((r) => r.ritual_id))
}

// Aliases por-tipo preservando los nombres usados antes de la generalización.
export const getMisReaccionesArengas = (playerId, ids) => getMisReacciones(playerId, 'arenga', ids)
export const getMisAlPastoArengas = (playerId, ids) => getMisAlPasto(playerId, 'arenga', ids)

// Variantes user-level: resuelve todos los player_id del user (multi-grupo)
// y agrega resultados. Útil en /perfil (arengas guardadas pueden ser de
// distintos grupos del usuario o de la Tribuna pública).
export const getMisReaccionesArengasPorUser = async (userId, arengaIds) => {
  if (!userId || !arengaIds?.length) return {}
  const jugRes = await supabase.from('jugadores').select('player_id').eq('user_id', userId)
  if (jugRes.error) { console.error('[rituales] getMisReaccionesArengasPorUser:', jugRes.error.message); return {} }
  const playerIds = (jugRes.data || []).map((j) => j.player_id)
  if (!playerIds.length) return {}
  const { data, error } = await supabase
    .from('reacciones_ritual')
    .select('ritual_id, tipo_reaccion')
    .in('player_id', playerIds)
    .eq('ritual_tipo', 'arenga')
    .in('ritual_id', arengaIds)
    .neq('tipo_reaccion', 'al_pasto')
  if (error) { console.error('[rituales] getMisReaccionesArengasPorUser:', error.message); return {} }
  const out = {}
  for (const row of data || []) out[row.ritual_id] = row.tipo_reaccion
  return out
}

export const getMisAlPastoArengasPorUser = async (userId, arengaIds) => {
  if (!userId || !arengaIds?.length) return new Set()
  const jugRes = await supabase.from('jugadores').select('player_id').eq('user_id', userId)
  if (jugRes.error) { console.error('[rituales] getMisAlPastoArengasPorUser:', jugRes.error.message); return new Set() }
  const playerIds = (jugRes.data || []).map((j) => j.player_id)
  if (!playerIds.length) return new Set()
  const { data, error } = await supabase
    .from('reacciones_ritual')
    .select('ritual_id')
    .in('player_id', playerIds)
    .eq('ritual_tipo', 'arenga')
    .in('ritual_id', arengaIds)
    .eq('tipo_reaccion', 'al_pasto')
  if (error) { console.error('[rituales] getMisAlPastoArengasPorUser:', error.message); return new Set() }
  return new Set((data || []).map((r) => r.ritual_id))
}

// Scoring Ritualístico v1.0 §11 / R9: "Mi historial tribunero" cuenta
// reacciones EMITIDAS por el USUARIO (todos sus player_id en todos los
// grupos), agrupadas por ritual_tipo. Mismo criterio de dedupe que el
// Ranking Nacional (Ranking v1.2 §4.2): la persona es una sola, sus
// reacciones se suman a través de todos los memberships.
// Sin feed histórico, sin badges, solo 3 contadores agregados.
export const contarReaccionesEmitidas = async (userId) => {
  const vacio = { arenga: 0, cabala: 0, promesa: 0 }
  if (!userId) return vacio
  // Resolver todos los player_id del user_id (un player_id por membership).
  const jugRes = await supabase
    .from('jugadores')
    .select('player_id')
    .eq('user_id', userId)
  if (jugRes.error) { console.error('[rituales] contarReaccionesEmitidas (jugadores):', jugRes.error.message); return vacio }
  const playerIds = (jugRes.data || []).map((j) => j.player_id)
  if (!playerIds.length) return vacio
  const { data, error } = await supabase
    .from('reacciones_ritual')
    .select('ritual_tipo')
    .in('player_id', playerIds)
  if (error) { console.error('[rituales] contarReaccionesEmitidas:', error.message); return vacio }
  const out = { ...vacio }
  for (const row of data || []) {
    if (row.ritual_tipo in out) out[row.ritual_tipo]++
  }
  return out
}

// La reacción de un jugador a un ritual (la real, no 'al_pasto') — o null.
export const getMiReaccion = async (playerId, ritualTipo, ritualId) => {
  if (!playerId || !ritualId) return null
  const { data, error } = await supabase
    .from('reacciones_ritual')
    .select('*')
    .eq('ritual_tipo', ritualTipo)
    .eq('ritual_id', ritualId)
    .eq('player_id', playerId)
  if (error) { console.error('[rituales] getMiReaccion:', error.message); return null }
  return (data || []).find((r) => r.tipo_reaccion !== 'al_pasto') || null
}

// ─── Arengas guardadas (perfil del usuario) ────────────────────────────────
// Cualquier usuario logueado puede guardar una arenga en su perfil. La RLS de
// `arengas_guardadas` opera por user_id = auth.uid() — INSERT/SELECT/DELETE
// directo. UNIQUE (user_id, arenga_id): guardar dos veces es idempotente.

export const guardarArenga = async (userId, arengaId) => {
  const { data, error } = await supabase
    .from('arengas_guardadas')
    .insert({ user_id: userId, arenga_id: arengaId })
    .select()
  if (!error) invalidarCacheRituales()
  if (error && error.code === '23505') return { data: true } // ya estaba guardada
  return error ? { error } : { data: data?.[0] || true }
}

export const quitarArengaGuardada = async (userId, arengaId) => {
  const { error } = await supabase
    .from('arengas_guardadas')
    .delete()
    .eq('user_id', userId)
    .eq('arenga_id', arengaId)
  if (!error) invalidarCacheRituales()
  return error ? { error } : { data: true }
}

export const getArengasGuardadasDelUsuario = async (userId) => {
  if (!userId) return []
  return _guardadasMemo.run(userId, async () => {
    const { data, error } = await supabase
      .from('arengas_guardadas')
      .select('arenga_id, created_at')
      .eq('user_id', userId)
    if (error) { console.error('[rituales] getArengasGuardadasDelUsuario:', error.message); return [] }
    return data || []
  })
}

// Variante con detalle: trae la arenga + el creador + el grupo para renderear
// la sección "Mis arengas guardadas" en el perfil.
//
// Por qué un RPC SECURITY DEFINER (en vez del SELECT con JOIN directo): la RLS
// de `jugadores` y `grupos` filtra al jugador a "es_miembro_grupo()". Cuando
// el user guarda una arenga PÚBLICA de OTRO grupo, los embedded JOINs vienen
// null → el modal mostraba "De Jugador  Grupo" literal (bug detectado 28-may
// post-bloque). El RPC bypasea RLS pero valida defensa-en-profundidad:
// auth.uid()=p_user_id Y la arenga es visible para el user (miembro del grupo
// o pública).
export const getArengasGuardadasDetalle = async (userId) => {
  if (!userId) return []
  const { data, error } = await supabase.rpc('get_arengas_guardadas_detalle', {
    p_user_id: userId,
  })
  if (error) { console.error('[rituales] getArengasGuardadasDetalle:', error.message); return [] }
  return data || []
}
